import {
  Component,
  HostListener,
  ElementRef,
  Renderer2,
  signal,
  AfterViewInit,
  OnDestroy,
  Inject,
  PLATFORM_ID,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import { Router, RouterLink, NavigationEnd } from '@angular/router';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { filter, Subscription } from 'rxjs';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { AuthService } from '../../../core/services/auth.service';

@Component({
  selector: 'app-site-header',
  standalone: true,
  imports: [CommonModule, RouterLink, TranslateModule, FormsModule],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class SiteHeaderComponent implements AfterViewInit, OnDestroy, OnInit {
  isMenuOpen = signal(false);
  
  isMobileMenuOpen = signal(false);
  showHeader = true;
  isScrolled = signal(false);

  private lastScrollY = 0;
  private ticking = false;
  private routerSub!: Subscription;
  private langChangeSub?: Subscription;
  private isBrowser: boolean;
  hasProductInCart = false;
  hasPricingWizardState = false;
  private checkInterval: any;
  private scrollUpShowTimer: ReturnType<typeof setTimeout> | null = null;

  languages = [
    { code: 'en', display: 'EN', full: 'English', flag: '/assets/flags/GB.svg' },
    { code: 'pl', display: 'PL', full: 'Polski', flag: '/assets/flags/PL.svg' },
    { code: 'de', display: 'DE', full: 'Deutsch', flag: '/assets/flags/DE.svg' },
    // { code: 'es', display: 'ES', full: 'Español', flag: '/assets/flags/ES.svg' },
    // { code: 'fr', display: 'FR', full: 'Français', flag: '/assets/flags/FR.svg' },
    // { code: 'de', display: 'DE', full: 'Deutsch', flag: '/assets/flags/DE.svg' },
  ];
  selectedLanguage = this.languages[0];
  languageMenuOpen = false;
  languageSearchQuery = '';
  showMobileLanguageModal = false;


  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private router: Router,
    private translate: TranslateService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);

    this.routerSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.showHeader =
          event.url !== '/login' && 
          event.url !== '/reset-password' && 
          event.url !== '/forgot-password' && 
          event.url !== '/otp-verification' &&
          event.url !== '/dashboard' &&
          event.url !== '/active-code';

        if (this.isBrowser) {
          setTimeout(() => this.updateHeaderOnScroll(), 0);
        }
      });
  }
  navLinks = [
    // { label: 'common.HOME', route: '/' },
    { label: 'common.HOW_IT_WORKS', route: '/how-it-works' },
    { label: 'common.ABOUT_US', route: '/about' },
    { label: 'common.PRICING', route: '/pricing' },
    // { label: 'FAQ', route: '/faq' },
    { label: 'common.CONTACT_US', route: '/contact' },
    // { label: 'common.CONTACT', route: '/contact' },
  ];


  ngOnInit() {
    // Initialize translation service
    this.translate.setFallbackLang('en');
    this.translate.setDefaultLang('en');

    // Load saved language or detect from IP
    if (this.isBrowser) {
      const savedLang = localStorage.getItem('selectedLanguage');

      if (savedLang) {
        // Use saved language preference
        const savedLanguage =
          this.languages.find((lang) => lang.code === savedLang) || this.languages[0];
        this.selectedLanguage = savedLanguage;
        this.setLanguage(savedLang);
      } else {
        // No saved preference - detect language from IP
        this.detectLanguageFromIP();
      }

      // Subscribe to language changes to update header when language changes
      this.langChangeSub = this.translate.onLangChange.subscribe(() => {
        this.cdr.detectChanges();
      });
    } else {
      // For SSR, ensure default language is set
      if (!this.translate.currentLang) {
        this.translate.use('en').subscribe({
          error: (err) => {
          }
        });
      }
    }

    // Check cart status and set up periodic checking
    this.checkCartStatus();
    if (this.isBrowser) {
      this.checkInterval = setInterval(() => {
        this.checkCartStatus();
      }, 500);
    }
  }

  private setLanguage(langCode: string): void {
    this.translate.use(langCode).subscribe({
      next: () => {
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.translate.use('en');
      }
    });
  }

  private detectLanguageFromIP(): void {
    // Default to English while fetching
    this.selectedLanguage = this.languages[0];
    this.setLanguage('en');

    // Try primary geolocation service
    this.tryGeolocationService('https://ipapi.co/json/', 'country_code')
      .catch(() => {
        // Fallback to secondary service
        return this.tryGeolocationService('https://api.ip.sb/jsonip', 'country_code');
      })
      .catch(() => {
        // Final fallback: use browser language
        this.detectLanguageFromBrowser();
      })
      .catch(err => {
        // Default to English on all errors
        localStorage.setItem('selectedLanguage', 'en');
      });
  }

  private tryGeolocationService(url: string, countryField: string): Promise<void> {
    return fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
      })
      .then(data => {
        const countryCode = data[countryField];

        // If Poland, set Polish; otherwise keep English
        if (countryCode === 'PL') {
          const polishLang = this.languages.find(lang => lang.code === 'pl');
          if (polishLang) {
            this.selectedLanguage = polishLang;
            localStorage.setItem('selectedLanguage', 'pl');
            this.setLanguage('pl');
          }
        } else {
          // Save English as preference so we don't check IP again
          localStorage.setItem('selectedLanguage', 'en');
        }
      });
  }

  private detectLanguageFromBrowser(): void {
    try {
      // Check browser language
      const browserLang = navigator.language || (navigator as any).userLanguage;
      const langCode = browserLang ? browserLang.split('-')[0] : 'en';

      if (langCode === 'pl') {
        const polishLang = this.languages.find(lang => lang.code === 'pl');
        if (polishLang) {
          this.selectedLanguage = polishLang;
          localStorage.setItem('selectedLanguage', 'pl');
          this.setLanguage('pl');
          return;
        }
      }

      // Default to English
      localStorage.setItem('selectedLanguage', 'en');
    } catch (err) {
      localStorage.setItem('selectedLanguage', 'en');
    }
  }

  loadTranslations() {
    // NOTE: This method is kept for backward compatibility
    // Translations are now loaded via loadTranslationsFromJson()
    this.translate.setFallbackLang('en');
    
    // If you need to manually set translations (not recommended), uncomment below:
    /*
    const translations = {
      en: {
        LOGO_TEXT: 'universeDynamic',
        HOME:"Home",
        HOW_IT_WORKS: 'How It Works',
        ABOUT_US:"About us",
        CONTACT_US: 'Contact Us',
        PRICING: 'Pricing',
        CONTACT: 'Contact',
        LOGIN: 'Login',
        LANGUAGE_SELECTOR: 'Language',
        CHOOSE_YOUR_LEGACY_PLAN: 'Choose Your Legacy Plan',
        SIMPLE_TRANSPARENT_PRICING: 'Simple, Transparent Pricing',
        PRICING_DESCRIPTION:
          "Create your own digital legacy for the future, or honor someone's memory with their AI avatar. Flexible plans designed for every relationship and every budget",
        FUTURE_LEGACY_PLANS: 'Future Legacy Plans (Create Your Avatar)',
        MEMORIAL_PLANS: "Memorial Plans (Honor Someone's Memory)",
        STARTER: 'Starter',
        CIRCLE: 'Circle',
        PREMIUM: 'Premium',
        ENTERPRISE: 'Enterprise',
        START_YOUR_LEGACY: 'Start Your Legacy',
        GET_CIRCLE_PLAN: 'Get Circle Plan',
        CHOOSE_PREMIUM: 'Choose Premium',
        CONTACT_SALES: 'Contact Sales',
        YOUR_LEGACY_LIVES_ON_FOREVER: 'Your Legacy Lives On Forever',
        AI_HEART_VOICE_WISDOM:
          'Create an AI that captures your heart, your voice, and your wisdom. Be there for every milestone, every moment, every memory your loved ones make - family, friends, and everyone who matters to you.',
        START_MY_LEGACY_50_OFF: 'Start My Legacy - 50% Off',
        SEE_HOW_IT_WORKS: 'See How It Works',
        PEOPLE_CONNECTED: 'People Connected',
        MEMORIES_PROTECTED: 'Memories Protected',
        SMILES_SHARED_DAILY: 'Smiles shared daily',
        LOVE_THAT_NEVER_ENDS: 'Love That Never Ends',
        TECHNOLOGY_MAKES_IMPOSSIBLE_POSSIBLE:
          "Technology makes the impossible possible - your presence, guidance, and wisdom can live on through our advanced AI system. Create your future legacy or honor someone's cherished memory.",
        MESSAGES_ACROSS_TIME: 'Messages Across Time',
        SEND_WISHES_AND_GUIDANCE:
          'Send birthday wishes, graduation congratulations, wedding blessings, and guidance for decades to come. Your words will find them exactly when they need them most.',
        YOUR_VOICE_AND_PRESENCE: 'Your Voice & Presence',
        VOICE_AND_PRESENCE_ALWAYS:
          'People can hear your voice and feel your presence whenever they need it. Have real conversations, share stories, and provide comfort during important moments.',
        NEVER_MISS_A_MOMENT: 'Never Miss A Moment',
        AI_REMEMBERS_SPECIAL_DATES:
          'Be there for every first day of school, birthday, triumph and challenge. Your AI remembers special dates and celebrates with personalized messages of encouragement.',
        COMING_SOON: 'Coming Soon',
        DIGITAL_TIME_CAPSULES: 'Digital Time Capsules',
        CREATE_FUTURE_MESSAGES:
          'Create messages, videos, and memories that unlock automatically in the future. Set them to open on specific dates, birthdays, graduations, or life milestones.',
        SCHEDULE_MESSAGES_IN_ADVANCE: 'Schedule messages years in advance',
        BIRTHDAY_SURPRISE_ON_TIME: 'Birthday surprises that arrive on time',
        GRADUATION_WISDOM: 'Graduation wisdom when they need it most',
        WEDDING_BLESSINGS_PAST: 'Wedding day blessings from the past',
        ENCRYPTED_PERFECT_MOMENT: 'Encrypted until the perfect moment',
        LEARN_ABOUT_TIME_CAPSULES: 'Learn About Time Capsules',
        TODAY: 'Today',
        CREATE_MESSAGES: 'Create Messages',
        EIGHTEENTH_BIRTHDAY: '18th Birthday',
        COMING_OF_AGE: 'Coming of Age',
        WEDDING_DAY: 'Wedding Day',
        LOVE_AND_MARRIAGE: 'Love & Marriage',
        GRANDCHILDREN: 'Grandchildren',
        FAMILY_LEGACY: 'Family Legacy',
        SIMPLE_PROCESS: 'Simple Process',
        CREATING_DIGITAL_PRESENCE: 'Creating your digital presence in just 4 meaningful steps',
        SHARE_YOUR_STORIES: 'Share Your Stories',
        RECORD_YOUR_VOICE: 'Record your voice, experiences, and personality',
        AI_LEARNS_YOU: 'AI Learns You',
        TECHNOLOGY_CAPTURES_ESSENCE: 'Technology captures your unique essence',
        CONNECT_PEOPLE: 'Connect People',
        INVITE_OTHERS: 'Invite others to your digital presence',
        ALWAYS_AVAILABLE: 'Always Available',
        BE_THERE_FOR_MOMENT: 'Be there for every important moment',
        WANT_TO_SEE_COMPLETE_PROCESS: 'Want to See the Complete Process?',
        LEARN_HOW_TECHNOLOGY_PRESERVES:
          'Learn exactly how our technology preserves your presence with detailed step-by-step guide',
        VIEW_COMPLETE_GUIDE: 'View Complete Guide & Tutorial',
        // Pricing Section Start
        // Pricing Section End
        //Showcase Section
        YOUR_LEGACY_DIGITALLY_PRESERVED: 'Your Legacy, Digitally Preserved',
        IMAGINE_YOUR_FRIEND_HEARING_YOUR_VOICE:
          "Imagine your best friend, years from now, hearing your voice say \"You've got this!\" during a tough time. Or your family receiving your wisdom exactly when they need guidance. This isn't just technology - it's a bridge between souls that transcends time and distance.",
        WATCH_REAL_STORIES: 'Watch Real Stories',
        INTERACTIVE_DEMO: 'Interactive Demo',
        VOICE_AND_VIDEO_CALLS: 'Voice & Video Calls',
        //Video Section
        INNOVATION: 'Innovation',
        SEE_CONNECTION_IN_ACTION: 'See Connection In Action',
        WATCH_HOW_AI_CREATES_CONNECTIONS:
          'Watch how our AI technology creates authentic connections. This 3-minute video shows the power of digital presence and how it brings comfort across time.',
        HEAR_AUTHENTIC_VOICE_PRESERVATION: 'Hear authentic voice preservation',
        SEE_NATURAL_CONVERSATION_FLOW: 'See natural conversation flow',
        EXPERIENCE_EMOTIONAL_CONNECTIONS: 'Experience emotional connections',
        UNDERSTAND_TECHNOLOGY_IMPACT: 'Understand the technology impact',
        WATCH_DEMO: 'Watch Demo',
      },
      pl: {
        LOGO_TEXT: 'Universe Dynamics',
        HOW_IT_WORKS: 'Jak to działa',
        PRICING: 'Cennik',
        CONTACT: 'Kontakt',
        LOGIN: 'Zaloguj się',
        LANGUAGE_SELECTOR: 'Język',
        CHOOSE_YOUR_LEGACY_PLAN: 'Wybierz swój plan dziedzictwa',
        SIMPLE_TRANSPARENT_PRICING: 'Proste, przejrzyste ceny',
        PRICING_DESCRIPTION:
          'Stwórz swoje cyfrowe dziedzictwo na przyszłość lub uczcij czyjąś pamięć za pomocą awatara AI. Elastyczne plany zaprojektowane dla każdej relacji i budżetu',
        FUTURE_LEGACY_PLANS: 'Plany przyszłego dziedzictwa (Stwórz swój awatar)',
        MEMORIAL_PLANS: 'Plany pamięci (Uczcij czyjąś pamięć)',
        STARTER: 'Starter',
        CIRCLE: 'Krąg',
        PREMIUM: 'Premium',
        ENTERPRISE: 'Przedsiębiorstwo',
        START_YOUR_LEGACY: 'Rozpocznij swoje dziedzictwo',
        GET_CIRCLE_PLAN: 'Uzyskaj plan Krąg',
        CHOOSE_PREMIUM: 'Wybierz Premium',
        CONTACT_SALES: 'Skontaktuj się ze sprzedażą',
        YOUR_LEGACY_LIVES_ON_FOREVER: 'Twoje dziedzictwo żyje wiecznie',
        AI_HEART_VOICE_WISDOM:
          'Stwórz sztuczną inteligencję, która uchwyci twoje serce, twój głos i twoją mądrość. Bądź obecny na każdym etapie, w każdej chwili, przy każdym wspomnieniu tworzonym przez twoich bliskich – rodzinę, przyjaciół i wszystkich, którzy są dla ciebie ważni.',
        START_MY_LEGACY_50_OFF: 'Rozpocznij moje dziedzictwo - 50% zniżki',
        SEE_HOW_IT_WORKS: 'Zobacz, jak to działa',
        PEOPLE_CONNECTED: 'Ludzie połączeni',
        MEMORIES_PROTECTED: 'Wspomnienia chronione',
        SMILES_SHARED_DAILY: 'Codziennie dzielone uśmiechy',
        LOVE_THAT_NEVER_ENDS: 'Miłość, która nigdy się nie kończy',
        TECHNOLOGY_MAKES_IMPOSSIBLE_POSSIBLE:
          'Technologia sprawia, że niemożliwe staje się możliwe – twoja obecność, wskazówki i mądrość mogą żyć dalej dzięki naszemu zaawansowanemu systemowi AI. Stwórz swoje przyszłe dziedzictwo lub uhonoruj czyjeś cenne wspomnienie.',
        MESSAGES_ACROSS_TIME: 'Wiadomości w czasie',
        SEND_WISHES_AND_GUIDANCE:
          'Wysyłaj życzenia urodzinowe, gratulacje z okazji ukończenia szkoły, błogosławieństwa ślubne i wskazówki na kolejne dekady. Twoje słowa dotrą do nich dokładnie wtedy, gdy będą ich najbardziej potrzebować.',
        YOUR_VOICE_AND_PRESENCE: 'Twój głos i obecność',
        VOICE_AND_PRESENCE_ALWAYS:
          'Ludzie mogą usłyszeć twój głos i poczuć twoją obecność zawsze, gdy tego potrzebują. Prowadź prawdziwe rozmowy, dziel się historiami i zapewniaj pocieszenie w ważnych momentach.',
        NEVER_MISS_A_MOMENT: 'Nie przegap żadnej chwili',
        AI_REMEMBERS_SPECIAL_DATES:
          'Bądź obecny przy każdym pierwszym dniu szkoły, urodzinach, triumfach i wyzwaniach. Twoja AI pamięta specjalne daty i świętuje je spersonalizowanymi wiadomościami pełnymi wsparcia.',
        COMING_SOON: 'Wkrótce',
        DIGITAL_TIME_CAPSULES: 'Cyfrowe kapsuły czasu',
        CREATE_FUTURE_MESSAGES:
          'Twórz wiadomości, filmy i wspomnienia, które odblokują się automatycznie w przyszłości. Ustaw je tak, aby otwierały się w określone dni, na urodziny, ukończenie szkoły lub ważne życiowe kamienie milowe.',
        SCHEDULE_MESSAGES_IN_ADVANCE: 'Zaplanuj wiadomości na wiele lat do przodu',
        BIRTHDAY_SURPRISE_ON_TIME: 'Niespodzianki urodzinowe, które przychodzą na czas',
        GRADUATION_WISDOM: 'Mądrość z okazji ukończenia szkoły, gdy jest najbardziej potrzebna',
        WEDDING_BLESSINGS_PAST: 'Błogosławieństwa ślubne z przeszłości',
        ENCRYPTED_PERFECT_MOMENT: 'Zaszyfrowane do idealnego momentu',
        LEARN_ABOUT_TIME_CAPSULES: 'Dowiedz się o kapsułach czasu',
        TODAY: 'Dzisiaj',
        CREATE_MESSAGES: 'Twórz wiadomości',
        EIGHTEENTH_BIRTHDAY: '18. urodziny',
        COMING_OF_AGE: 'Pełnoletność',
        WEDDING_DAY: 'Dzień ślubu',
        LOVE_AND_MARRIAGE: 'Miłość i małżeństwo',
        GRANDCHILDREN: 'Wnuki',
        FAMILY_LEGACY: 'Dziedzictwo rodziny',
        SIMPLE_PROCESS: 'Prosty proces',
        CREATING_DIGITAL_PRESENCE:
          'Tworzenie swojej cyfrowej obecności w zaledwie 4 znaczących krokach',
        SHARE_YOUR_STORIES: 'Podziel się swoimi historiami',
        RECORD_YOUR_VOICE: 'Nagrywaj swój głos, doświadczenia i osobowość',
        AI_LEARNS_YOU: 'AI Cię poznaje',
        TECHNOLOGY_CAPTURES_ESSENCE: 'Technologia uchwytuje twoją unikalną istotę',
        CONNECT_PEOPLE: 'Łącz ludzi',
        INVITE_OTHERS: 'Zaproś innych do swojej cyfrowej obecności',
        ALWAYS_AVAILABLE: 'Zawsze dostępny',
        BE_THERE_FOR_MOMENT: 'Bądź obecny przy każdej ważnej chwili',
        WANT_TO_SEE_COMPLETE_PROCESS: 'Chcesz zobaczyć cały proces?',
        LEARN_HOW_TECHNOLOGY_PRESERVES:
          'Dowiedz się dokładnie, jak nasza technologia zachowuje twoją obecność dzięki szczegółowemu przewodnikowi krok po kroku',
        VIEW_COMPLETE_GUIDE: 'Zobacz pełny przewodnik i tutorial',
        // Pricing Section Start
        // Pricing Section End
        //showcase
        YOUR_LEGACY_DIGITALLY_PRESERVED: 'Twoje dziedzictwo, cyfrowo zachowane',
        IMAGINE_YOUR_FRIEND_HEARING_YOUR_VOICE:
          'Wyobraź sobie swojego najlepszego przyjaciela, za kilka lat, słyszącego twój głos mówiący "Dasz radę!" w trudnym czasie. Albo twoja rodzina otrzymująca twoją mądrość dokładnie wtedy, gdy potrzebuje wskazówek. To nie tylko technologia – to most między duszami, który przekracza czas i odległość.',
        WATCH_REAL_STORIES: 'Obejrzyj prawdziwe historie',
        INTERACTIVE_DEMO: 'Interaktywny pokaz',
        VOICE_AND_VIDEO_CALLS: 'Połączenia głosowe i wideo',
        //Video Section
        INNOVATION: 'Innowacja',
        SEE_CONNECTION_IN_ACTION: 'Zobacz połączenie w akcji',
        WATCH_HOW_AI_CREATES_CONNECTIONS:
          'Zobacz, jak nasza technologia AI tworzy autentyczne połączenia. Ten 3-minutowy film pokazuje siłę cyfrowej obecności i jak przynosi komfort w czasie.',
        HEAR_AUTHENTIC_VOICE_PRESERVATION: 'Usłysz autentyczne zachowanie głosu',
        SEE_NATURAL_CONVERSATION_FLOW: 'Zobacz naturalny przebieg rozmowy',
        EXPERIENCE_EMOTIONAL_CONNECTIONS: 'Doświadcz emocjonalnych połączeń',
        UNDERSTAND_TECHNOLOGY_IMPACT: 'Zrozum wpływ technologii',
        WATCH_DEMO: 'Obejrzyj pokaz',
      },

      es: {
        LOGO_TEXT: 'Universe Dynamics',
        HOW_IT_WORKS: 'Cómo funciona',
        PRICING: 'Precios',
        CONTACT: 'Contacto',
        LOGIN: 'Iniciar sesión',
        LANGUAGE_SELECTOR: 'Idioma',
        CHOOSE_YOUR_LEGACY_PLAN: 'Elige tu plan de legado',
        SIMPLE_TRANSPARENT_PRICING: 'Precios simples y transparentes',
        PRICING_DESCRIPTION:
          'Crea tu legado digital para el futuro, o honra la memoria de alguien con su avatar de IA. Planes flexibles diseñados para cada relación y presupuesto',
        FUTURE_LEGACY_PLANS: 'Planes de legado futuro (Crea tu avatar)',
        MEMORIAL_PLANS: 'Planes conmemorativos (Honra la memoria de alguien)',
        STARTER: 'Inicial',
        CIRCLE: 'Círculo',
        PREMIUM: 'Premium',
        ENTERPRISE: 'Empresa',
        START_YOUR_LEGACY: 'Comienza tu legado',
        GET_CIRCLE_PLAN: 'Obtener plan Círculo',
        CHOOSE_PREMIUM: 'Elegir Premium',
        CONTACT_SALES: 'Contactar ventas',
        YOUR_LEGACY_LIVES_ON_FOREVER: 'Tu legado vive eternamente',
        AI_HEART_VOICE_WISDOM:
          'Crea una IA que capture tu corazón, tu voz y tu sabiduría. Estate presente en cada hito, en cada momento, en cada recuerdo que creen tus seres queridos: familia, amigos y todos los que importan para ti.',
        START_MY_LEGACY_50_OFF: 'Comienza mi legado - 50% de descuento',
      },
      fr: {
        LOGO_TEXT: 'Universe Dynamics',
        HOW_IT_WORKS: 'Comment ça marche',
        PRICING: 'Tarifs',
        CONTACT: 'Contact',
        LOGIN: 'Se connecter',
        LANGUAGE_SELECTOR: 'Langue',
        CHOOSE_YOUR_LEGACY_PLAN: "Choisissez votre plan d'héritage",
        SIMPLE_TRANSPARENT_PRICING: 'Tarifs simples et transparents',
        PRICING_DESCRIPTION:
          "Créez votre héritage numérique pour l'avenir, ou honorez la mémoire de quelqu'un avec son avatar IA. Plans flexibles conçus pour chaque relation et budget",
        FUTURE_LEGACY_PLANS: "Plans d'héritage futur (Créez votre avatar)",
        MEMORIAL_PLANS: "Plans commémoratifs (Honorez la mémoire de quelqu'un)",
        STARTER: 'Débutant',
        CIRCLE: 'Cercle',
        PREMIUM: 'Premium',
        ENTERPRISE: 'Entreprise',
        START_YOUR_LEGACY: 'Commencez votre héritage',
        GET_CIRCLE_PLAN: 'Obtenir le plan Cercle',
        CHOOSE_PREMIUM: 'Choisir Premium',
        CONTACT_SALES: 'Contacter les ventes',
        YOUR_LEGACY_LIVES_ON_FOREVER: 'Votre héritage vit éternellement',
        AI_HEART_VOICE_WISDOM:
          'Créez une IA qui capture votre cœur, votre voix et votre sagesse. Soyez là pour chaque étape, chaque moment, chaque souvenir que vos proches créent – famille, amis et tous ceux qui comptent pour vous.',
        START_MY_LEGACY_50_OFF: 'Commencez mon héritage - 50% de réduction',
      },
      de: {
        LOGO_TEXT: 'Universe Dynamics',
        HOW_IT_WORKS: 'Wie es funktioniert',
        PRICING: 'Preise',
        CONTACT: 'Kontakt',
        LOGIN: 'Anmelden',
        LANGUAGE_SELECTOR: 'Sprache',
        CHOOSE_YOUR_LEGACY_PLAN: 'Wählen Sie Ihren Vermächtnis-Plan',
        SIMPLE_TRANSPARENT_PRICING: 'Einfache, transparente Preise',
        PRICING_DESCRIPTION:
          'Erstellen Sie Ihr digitales Vermächtnis für die Zukunft oder ehren Sie jemandes Erinnerung mit seinem KI-Avatar. Flexible Pläne für jede Beziehung und jedes Budget',
        FUTURE_LEGACY_PLANS: 'Zukunfts-Vermächtnis-Pläne (Erstellen Sie Ihren Avatar)',
        MEMORIAL_PLANS: 'Gedenkpläne (Ehren Sie jemandes Erinnerung)',
        STARTER: 'Starter',
        CIRCLE: 'Kreis',
        PREMIUM: 'Premium',
        ENTERPRISE: 'Unternehmen',
        START_YOUR_LEGACY: 'Beginnen Sie Ihr Vermächtnis',
        GET_CIRCLE_PLAN: 'Kreis-Plan erhalten',
        CHOOSE_PREMIUM: 'Premium wählen',
        CONTACT_SALES: 'Vertrieb kontaktieren',
        YOUR_LEGACY_LIVES_ON_FOREVER: 'Ihr Erbe lebt ewig',
        AI_HEART_VOICE_WISDOM:
          'Erschaffe eine KI, die dein Herz, deine Stimme und deine Weisheit einfängt. Sei da für jeden Meilenstein, jeden Moment, jede Erinnerung, die deine Lieben schaffen – Familie, Freunde und alle, die dir wichtig sind.',
        START_MY_LEGACY_50_OFF: 'Starte mein Vermächtnis - 50% Rabatt',
      },
    };
    
    // Set translations for each language
    Object.keys(translations).forEach((lang) => {
      this.translate.setTranslation(lang, (translations as any)[lang]);
    });
    */
  }

  toggleLanguageMenu() {
    if (!this.isBrowser) return;
    
    // Check if mobile
    const isMobile = window.innerWidth <= 1050;
    
    if (isMobile) {
      // Show mobile modal
      const wasOpen = this.showMobileLanguageModal;
      this.showMobileLanguageModal = !this.showMobileLanguageModal;
      
      // Disable/enable scroll based on modal state
      // If mobile menu is also open, don't toggle scroll (let mobile menu handle it)
      if (!this.isMobileMenuOpen()) {
        this.toggleBodyScroll(!this.showMobileLanguageModal);
      }
    } else {
      // Desktop dropdown
      this.languageMenuOpen = !this.languageMenuOpen;
    }
  }

  closeMobileLanguageModal(): void {
    this.showMobileLanguageModal = false;
    this.languageSearchQuery = '';
    
    // Only enable scroll if mobile menu is also closed
    // If mobile menu is still open, keep scroll disabled
    if (!this.isMobileMenuOpen()) {
      this.toggleBodyScroll(true);
    }
  }

  getFilteredLanguages() {
    if (!this.languageSearchQuery.trim()) {
      return this.languages;
    }
    const query = this.languageSearchQuery.toLowerCase();
    return this.languages.filter(lang => 
      lang.full.toLowerCase().includes(query) ||
      lang.code.toLowerCase().includes(query) ||
      lang.display.toLowerCase().includes(query)
    );
  }

  selectLanguage(lang: any) {
    this.selectedLanguage = lang;
    
    // Store the selected language in localStorage for persistence
    if (this.isBrowser) {
      localStorage.setItem('selectedLanguage', lang.code);
    }

    // Switch the translation language and wait for it to load
    this.translate.use(lang.code).subscribe({
      next: () => {
        // Force change detection after language change
        this.cdr.detectChanges();
      },
      error: (err) => {
        // Fallback to English if loading fails
        this.translate.use('en');
      }
    });

    // Close desktop dropdown if open
    if (this.languageMenuOpen && !this.showMobileLanguageModal) {
      this.languageMenuOpen = false;
    }
    
    // Close mobile modal if open (when language is selected on mobile)
    if (this.showMobileLanguageModal) {
      this.closeMobileLanguageModal();
    }
    
    // Close mobile menu if open (when language is changed on mobile device)
    if (this.isBrowser && this.isMobileMenuOpen()) {
      const isMobile = window.innerWidth <= 1050;
      if (isMobile) {
        this.closeMobileMenu();
      }
    }
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    if (!this.isBrowser) return;

    const dropdown = this.el.nativeElement.querySelector('.language-dropdown');
    const mobileDropdown = this.el.nativeElement.querySelector('.mobile-language-dropdown');

    if (
      dropdown &&
      !dropdown.contains(event.target as Node) &&
      mobileDropdown &&
      !mobileDropdown.contains(event.target as Node)
    ) {
      this.languageMenuOpen = false;
    }
  }

  toggleMenu(): void {
    this.isMenuOpen.update((v) => !v);
  }

  closeMenu(): void {
    this.isMenuOpen.set(false);
  }

  toggleMobileMenu(): void {
    const newValue = !this.isMobileMenuOpen();
    this.isMobileMenuOpen.set(newValue);
    this.toggleBodyScroll(!newValue);
    
    // Preserve scrolled state when menu opens
    // Don't let scroll events change the scrolled class while menu is open
    if (newValue && this.isBrowser) {
      // Menu is opening - preserve current scrolled state
      // The scrolled class will be maintained via [class.scrolled]="isScrolled()" binding
    }
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen.set(false);
    this.toggleBodyScroll(true);
  }

  private toggleBodyScroll(enable: boolean): void {
    if (!this.isBrowser) return;
    
    if (enable) {
      // Enable scroll and restore position
      const scrollY = (document.body as any).scrollY || 0;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      
      // Restore scroll position
      if (scrollY) {
        window.scrollTo(0, scrollY);
      }
    } else {
      // Disable scroll - save current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      // Store scroll position for restoration
      (document.body as any).scrollY = scrollY;
    }
  }

  isLoggedIn(): boolean {
    return this.authService.isAuthenticated();
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;

    const anchors = this.el.nativeElement.querySelectorAll('a[href^="#"]');
    anchors.forEach((anchor: HTMLElement) => {
      this.renderer.listen(anchor, 'click', (e: Event) => {
        e.preventDefault();
        const href = (anchor as HTMLAnchorElement).getAttribute('href');
        if (href) {
          const target = document.querySelector(href);
          if (target) {
            const headerHeight = this.el.nativeElement.offsetHeight;
            const targetPosition = (target as HTMLElement).offsetTop - headerHeight - 20;
            // window.scrollTo({ top: targetPosition, behavior: 'smooth' });
          }
        }
      });
    });

    this.updateHeaderOnScroll();
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (!this.isBrowser) return;

    // Don't update header on scroll if mobile menu or language modal is open
    if (this.isMobileMenuOpen() || this.showMobileLanguageModal) {
      return;
    }

    if (!this.ticking) {
      requestAnimationFrame(() => this.updateHeaderOnScroll());
      this.ticking = true;
    }
  }

  private updateHeaderOnScroll() {
    if (!this.isBrowser) return;

    // Don't update header if mobile menu or language modal is open
    if (this.isMobileMenuOpen() || this.showMobileLanguageModal) {
      return;
    }

    const header: HTMLElement = this.el.nativeElement.querySelector('header');
    if (!header) return;

    const scrollY = window.scrollY;
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercent = maxScroll > 0 ? scrollY / maxScroll : 0;

    const progressBar = document.querySelector('.scroll-progress') as HTMLElement;
    if (progressBar) {
      progressBar.style.width = Math.min(scrollPercent * 100, 100) + '%';
    }

    const isScrollingUp = scrollY < this.lastScrollY;

    if (scrollY <= 50) {
      if (this.scrollUpShowTimer) {
        clearTimeout(this.scrollUpShowTimer);
        this.scrollUpShowTimer = null;
      }
      header.classList.remove('scrolled', 'hidden');
      this.isScrolled.set(false);
    } else if (!isScrollingUp && scrollY > 100) {
      if (this.scrollUpShowTimer) {
        clearTimeout(this.scrollUpShowTimer);
        this.scrollUpShowTimer = null;
      }
      header.classList.add('hidden');
      header.classList.remove('scrolled');
      this.isScrolled.set(false);
    } else if (isScrollingUp && scrollY > 100) {
      if (this.scrollUpShowTimer) {
        clearTimeout(this.scrollUpShowTimer);
        this.scrollUpShowTimer = null;
      }
      header.classList.remove('hidden');
      header.classList.add('scrolled');
      this.isScrolled.set(true);
    }

    this.lastScrollY = scrollY;
    this.ticking = false;
  }

  checkCartStatus(): void {
    if (!this.isBrowser) return;

    try {
      const savedState = localStorage.getItem('pricingWizardState');
      if (savedState) {
        this.hasPricingWizardState = true;
        const state = JSON.parse(savedState);
        // Check if there's a selected package (product in cart)
        this.hasProductInCart = !!(state.selectedPackage || state.staticSelectedPlan);
      } else {
        this.hasPricingWizardState = false;
        this.hasProductInCart = false;
      }
    } catch (error) {
      this.hasPricingWizardState = false;
      this.hasProductInCart = false;
    }
  }

  onCartClick(): void {
    // Clear any startFresh flag to ensure state is restored
    // Cart button should restore the saved step from pricingWizardState
    if (this.isBrowser) {
      sessionStorage.removeItem('pricingStartFresh');
    }
    // Navigate to pricing page - the component will restore state automatically
    // The pricing component's restorePricingState() will handle step restoration from localStorage
    this.router.navigate(['/pricing']);
  }

  onPricingLinkClick(): void {
    // Set a flag to start fresh at step 1 when clicking pricing menu
    // Don't remove pricingWizardState, but signal to ignore it and start at step 1
    // Cart button and view order will restore state normally
    if (this.isBrowser) {
      sessionStorage.setItem('pricingStartFresh', 'true');
    }
    // Navigation will happen via routerLink, so we don't need to navigate here
  }

  ngOnDestroy() {
    if (this.routerSub) this.routerSub.unsubscribe();
    if (this.langChangeSub) this.langChangeSub.unsubscribe();
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
    if (this.scrollUpShowTimer) {
      clearTimeout(this.scrollUpShowTimer);
    }
    // Ensure scroll is enabled when component is destroyed
    if (this.isBrowser) {
      const scrollY = (document.body as any).scrollY || 0;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      if (scrollY) {
        window.scrollTo(0, scrollY);
      }
    }
  }
}
